import React, { HTMLInputTypeAttribute, useCallback, useMemo } from 'react';
import InputLabel from '../input-label/InputLabel';
import { getClassNames } from '../../helpers/classHelpers';
import { generateUniqueId } from '@danfoss/mosaic-core';
import { withUIState } from '../../contexts/ui-state/withUIState';

export type InputProps = {
    id?: string;
    label?: string;

    // TODO: Make value generic to accept different types
    value: string;
    onChange?: (value: string) => void;
    onBlur?: (value: string) => void;
    onFocus?: (value: string) => void;
    placeholder?: string;
    type?: HTMLInputTypeAttribute;

    errorVisibleAfterBlur?: boolean;
    hasError?: boolean;
    helperText?: string;
    focusedHelperText?: string;

    isReadonly?: boolean;
    isLoading?: boolean;
    isDisabled?: boolean;
    className?: string;
    containerClassName?: string;

    maxLength?: number;
};

const Input: React.FC<InputProps> = ({ id, label, value, onChange, onFocus, onBlur, placeholder, type = 'text', hasError, helperText, isDisabled, isLoading, isReadonly, className, containerClassName, errorVisibleAfterBlur, maxLength, focusedHelperText }) => {
    const localId = useMemo(() => id ?? generateUniqueId(), [id]);

    const [blurred, setBlurred] = React.useState<boolean>();

    const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        onFocus?.(e.target.value ?? "");
        setBlurred(false);
    }, [onFocus]);

    const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        onBlur?.(e.target.value ?? "");
        setBlurred(true);
    }, [onBlur]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (type === 'number') {
            const value = Number(e.target.value);
            onChange?.((value < 1 ? NaN : value) as any);
        }
        else {
            onChange?.(e.target.value ?? "");
        }
    }, [onChange, type]);

    return (
        <div className={containerClassName}>
            {label && <InputLabel className={'df-field-label'} htmlFor={localId} label={label} isDisabled={isDisabled} />}
            <input
                id={localId}
                type={type}
                placeholder={placeholder}
                className={getClassNames(['df-input', (hasError && (!errorVisibleAfterBlur || blurred)) && 'error', className])}
                disabled={isDisabled || isLoading}
                readOnly={isReadonly}
                value={value}
                onChange={handleChange}

                onFocus={handleFocus}
                onBlur={handleBlur}

                maxLength={maxLength}

                // TODO: Only if type is equals to number
                min={1}
            />
            {blurred === false && focusedHelperText && <label className='df-helper-text'>{focusedHelperText}</label>}
            {helperText && (!errorVisibleAfterBlur || blurred) && <label className={getClassNames(['df-helper-text', hasError && 'error'])}>{helperText}</label>}
        </div>
    );
};

export default withUIState(Input);